/** V2AchPitchPage — /pitch/:token (template = ach_hire_system)
 *  Private, tokenised, noindex hosted proposal for All Cranes Hire (Todd).
 *  LIGHT theme, ACH brand tokens. Reusable proposal-experience template:
 *  every brand token lives in the top-level `A` constant map, so a new
 *  client rebrand = swap the token block.
 *  Kindra bookends (intro + close) use the Kindra navy + spectrum.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

// ── ACH brand tokens (swap this block per client) ────────────────────
const A = {
  bg:        '#f7f5f8',        // warm off-white base
  surface:   '#ffffff',        // card
  border:    '#e6e1e8',        // hairline
  ink:       '#231f20',        // near-black
  ink2:      '#414042',        // charcoal
  ink3:      '#6a6669',        // muted
  ink4:      '#9a9497',        // soft
  brand:     '#8d1b4f',        // ACH magenta/burgundy
  brandHi:   '#a83568',        // hover
  brandLo:   '#6a1039',        // deeper
  brandTint: '#f4e3ec',        // brand background tint
  ok:        '#2f8f5f',
  warn:      '#c88a2b',
  err:       '#b53a3a',
};
const A_LOGO = '/pitch-assets/ach/logo-inline-dark-text.png';

// ── Kindra bookend tokens ────────────────────────────────────────────
const K = {
  navy:     '#100838',
  navy2:    '#160c44',
  navyDeep: '#0a0424',
  icy:      '#DCF0F5',
  icyMute:  'rgba(220,240,245,0.72)',
  icyDim:   'rgba(220,240,245,0.5)',
};
const SPECTRUM = 'linear-gradient(95deg, #34D1B0 0%, #1FB6C1 25%, #4F8FD0 50%, #9156C4 75%, #EB258D 100%)';
const KINDRA_LOGO = '/brand/kindra-logo-dark.svg';

// ── Fonts + global styles ────────────────────────────────────────────
const AchStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; }
    html, body { background: ${A.bg}; margin: 0; }
    .ach { font-family: 'Inter', system-ui, sans-serif; color: ${A.ink}; }
    .ach h1, .ach h2, .ach h3, .ach h4 { font-family: 'Manrope', system-ui, sans-serif; margin: 0; letter-spacing: -0.015em; }
    .ach h1 { font-weight: 800; }
    .ach h2 { font-weight: 700; }

    @keyframes ach-in     { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes ach-lift   { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes ach-slide  { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
    @keyframes ach-bob    { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
    .ach-reveal { opacity: 0; will-change: opacity, transform; }
    .ach-reveal.is-in { animation: ach-in 640ms cubic-bezier(0.22,1,0.36,1) both; }
    .ach-reveal.lift.is-in { animation-name: ach-lift; }
    .ach-reveal.slide.is-in { animation-name: ach-slide; }
    .ach-reveal.d1.is-in { animation-delay: 60ms; }
    .ach-reveal.d2.is-in { animation-delay: 140ms; }
    .ach-reveal.d3.is-in { animation-delay: 220ms; }
    .ach-reveal.d4.is-in { animation-delay: 300ms; }
    .ach-reveal.d5.is-in { animation-delay: 380ms; }
    .ach-reveal.d6.is-in { animation-delay: 460ms; }
    @media (prefers-reduced-motion: reduce) {
      .ach-reveal, .ach-reveal.is-in { animation: none !important; opacity: 1 !important; transform: none !important; }
      .ach-cue { animation: none !important; }
    }
    .ach-cue { animation: ach-bob 2.4s ease-in-out infinite; }

    .ach-eyebrow { font-family: 'Inter', sans-serif; font-weight: 700; font-size: 11.5px; letter-spacing: 0.18em; text-transform: uppercase; color: ${A.brand}; }
    .ach-progress { position: fixed; top: 0; left: 0; height: 3px; background: ${A.brand}; z-index: 60; transition: width 120ms linear; }
    .ach-btn { font-family: 'Manrope', sans-serif; font-weight: 700; letter-spacing: 0.02em; padding: 15px 26px; border-radius: 999px; border: 1px solid transparent; cursor: pointer; font-size: 14px; transition: transform 220ms cubic-bezier(0.22,1,0.36,1), background 220ms ease, border-color 220ms ease; }
    .ach-btn.primary { background: ${A.brand}; color: #fff; }
    .ach-btn.primary:hover { background: ${A.brandHi}; transform: translateY(-2px); }
    .ach-btn.ghost { background: transparent; color: ${A.brand}; border-color: ${A.brand}; }
    .ach-btn.ghost:hover { background: ${A.brandTint}; transform: translateY(-2px); }

    .ach-card { background: ${A.surface}; border: 1px solid ${A.border}; border-radius: 16px; padding: 26px; }
    .ach-mock { background: ${A.surface}; border: 1px solid ${A.border}; border-radius: 18px; overflow: hidden; box-shadow: 0 24px 60px -32px rgba(35,31,32,0.24); }
    /* Only stretch DIRECT-child mockup images (real screenshots) to
       full width. Nested logos inside the placeholder sidebar keep
       their intrinsic size — the earlier * img selector was squashing
       the ACH logo across the mockup header. */
    .ach-mock > img { display: block; width: 100%; height: auto; cursor: zoom-in; transition: transform 320ms ease; }
    .ach-mock:hover > img { transform: scale(1.005); }

    /* Kindra bookend — light text on navy. Explicit colour on every
       heading level so the .ach h1..h4 rule above cannot cascade ACH
       ink into the bookend and go dark-on-dark. */
    .k-section { position: relative; overflow: hidden; background: radial-gradient(ellipse at 20% -10%, ${K.navy2} 0%, ${K.navy} 40%, ${K.navyDeep} 100%); color: ${K.icy}; padding: clamp(90px, 12vw, 140px) clamp(24px, 6vw, 80px); font-family: 'Manrope', system-ui, sans-serif; }
    .k-section h1, .k-section h2, .k-section h3, .k-section h4 { color: ${K.icy} !important; }
    .k-section p, .k-section li, .k-section span, .k-section a { color: ${K.icy}; }
    .k-spectrum { position: absolute; inset: 0; opacity: 0.14; background: ${SPECTRUM}; mix-blend-mode: screen; pointer-events: none; }
    .k-section h1 { font-size: clamp(38px, 5.6vw, 68px); line-height: 1.05; letter-spacing: -0.02em; font-weight: 800; margin: 22px 0 20px; }
    .k-section .k-lede { font-family: 'Inter'; font-size: 17px; line-height: 1.6; color: ${K.icyMute}; max-width: 640px; }
    .k-section .k-eyebrow { font-family: 'Inter'; font-size: 11.5px; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 700; color: ${K.icy}; background: linear-gradient(90deg, #34D1B0, #EB258D); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }

    .ach-lb { position: fixed; inset: 0; z-index: 80; background: rgba(15,10,25,0.85); backdrop-filter: blur(8px); display: grid; grid-template-rows: auto 1fr auto; }
    .ach-lb-bar { padding: 14px 22px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255,255,255,0.08); color: #fff; }
    .ach-lb-close { background: transparent; border: 1px solid rgba(255,255,255,0.24); color: #fff; width: 40px; height: 40px; border-radius: 999px; font-size: 22px; cursor: pointer; }
    .ach-lb-scroll { overflow: auto; padding: 22px; display: grid; place-items: center; }
    .ach-lb-scroll img { max-width: min(1400px, 100%); border-radius: 10px; box-shadow: 0 30px 70px rgba(0,0,0,0.5); }

    @media (max-width: 720px) {
      .ach-card { padding: 20px; }
      .ach h1, .ach h2 { letter-spacing: -0.01em; }
      .mock-inner { display: block !important; }
      .mock-inner.is-dash { display: grid !important; grid-template-columns: 1.6fr 1fr; gap: 18px; }
      .mock-kpi-row { grid-template-columns: repeat(2, 1fr) !important; }
      .ach-mobile-only { display: block !important; }
    }
    @media (min-width: 721px) {
      .mock-inner.is-dash { display: grid; grid-template-columns: 1.6fr 1fr; gap: 18px; }
    }
    @media (max-width: 520px) {
      .mock-inner.is-dash { grid-template-columns: 1fr !important; }
    }
  `}</style>
);

// ── Hooks ────────────────────────────────────────────────────────────
function useReveal(threshold = 0.18) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } });
    }, { threshold });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, cls: seen ? 'is-in' : '' };
}
function useSectionTracker(sectionKey, index, onSeen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.28) { onSeen(sectionKey, index); io.disconnect(); }
      });
    }, { threshold: [0.28, 0.5] });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [sectionKey, index, onSeen]);
  return ref;
}
function useCountUp(target, seen, duration = 1200) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!seen) return;
    const start = performance.now();
    const end = Number(target) || 0;
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.round(end * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, seen, duration]);
  return v;
}

const Reveal = ({ children, className = '', delay = 0 }) => {
  const r = useReveal();
  return <div ref={r.ref} className={`ach-reveal ${className} ${delay ? `d${delay}` : ''} ${r.cls}`}>{children}</div>;
};

// ── Section wrapper (ACH light) ──────────────────────────────────────
const AchSection = ({ id, sectionKey, index, onSeen, children, style, tone = 'light' }) => {
  const ref = useSectionTracker(sectionKey, index, onSeen);
  const bg = tone === 'tint' ? A.brandTint : tone === 'ink' ? A.ink : A.bg;
  return (
    <section ref={ref} id={id} data-section={sectionKey}
      style={{ background: bg, color: tone === 'ink' ? '#fff' : A.ink, padding: 'clamp(70px, 8vw, 110px) clamp(22px, 5vw, 72px)', ...style }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>{children}</div>
    </section>
  );
};

const AchHeader = ({ eyebrow }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap', marginBottom: 34 }}>
    <img src={A_LOGO} alt="All Cranes Hire" style={{ height: 44, width: 'auto' }} />
    {eyebrow && (
      <>
        <span aria-hidden style={{ display: 'inline-block', width: 1, height: 20, background: A.border }} />
        <span className="ach-eyebrow" data-testid={`ach-eyebrow-${eyebrow.replace(/\s+/g, '-').toLowerCase()}`}>{eyebrow}</span>
      </>
    )}
  </div>
);

// ── 1. KINDRA INTRO (navy bookend) ───────────────────────────────────
function KindraIntro({ onSeen, prepared_for }) {
  const ref = useSectionTracker('ach_intro', 0, onSeen);
  return (
    <section ref={ref} data-section="ach_intro" className="k-section">
      <div className="k-spectrum" />
      <div style={{ maxWidth: 1180, margin: '0 auto', position: 'relative' }}>
        <img src={KINDRA_LOGO} alt="Kindra AI" style={{ height: 40, display: 'block' }}
          onError={(e) => { e.target.style.display = 'none'; }} />
        <Reveal>
          <p className="k-eyebrow" style={{ marginTop: 40 }}>KINDRA · A PROPOSAL PREPARED FOR</p>
          <h1 style={{ marginTop: 12 }}>
            {(prepared_for || 'Todd').split(' ')[0]} — here&apos;s your own hire platform.
          </h1>
          <p className="k-lede">
            One system. The whole yard. This isn&apos;t a slide deck — it&apos;s the platform,
            in the browser, exactly as it will land at All Cranes Hire. Scroll it at your pace.
          </p>
        </Reveal>
        <div className="ach-cue" style={{ marginTop: 70, fontFamily: 'Inter', fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: K.icyDim }}>
          scroll ↓
        </div>
      </div>
    </section>
  );
}

// ── 2. HERO ──────────────────────────────────────────────────────────
function AchHero({ onSeen }) {
  return (
    <AchSection id="s-ach-hero" sectionKey="ach_hero" index={1} onSeen={onSeen} tone="light">
      <AchHeader eyebrow="the platform · at a glance" />
      <Reveal>
        <h1 style={{ fontSize: 'clamp(42px, 6vw, 78px)', lineHeight: 1.02, maxWidth: 900 }}>
          One system.<br /><span style={{ color: A.brand }}>The whole yard.</span>
        </h1>
      </Reveal>
      <Reveal delay={2}>
        <p style={{ fontFamily: 'Inter', fontSize: 18, color: A.ink2, marginTop: 26, maxWidth: 640, lineHeight: 1.6 }}>
          Quoting, hires, month-end, invoicing, compliance, transport, waitlist — every part of ACH
          on one screen. Built for how your team actually works, not how a generic hire tool wishes you did.
        </p>
      </Reveal>
    </AchSection>
  );
}

// ── 3. THE LEAKS ─────────────────────────────────────────────────────
const LEAKS = [
  { t: 'The seven-tab Excel', b: 'Fleet, hires, invoices, service, compliance — spread across tabs nobody trusts. A crane goes on hire in one place, invoices from another, and month-end is a hunt.' },
  { t: 'Viberent-as-middleman', b: 'Two systems to log the same hire. You pay for it, you retype into it, and it still can\'t answer "who\'s got what on hire right now?" without a phone call.' },
  { t: 'Quotes re-typed', b: 'Same crane, same rate card, same terms — typed fresh every time. Ten minutes per quote × twelve quotes a week = an afternoon a fortnight, gone.' },
  { t: 'Quotes go cold', b: 'You send it and hear nothing. No visibility on who opened it, when, or whether it\'s worth chasing. The follow-ups happen — sometimes.' },
  { t: 'Blind win rate', b: 'Nobody at ACH can tell you last month\'s quote-to-hire conversion without a spreadsheet build. So decisions get made on gut, not on numbers.' },
  { t: 'Debtors by memory', b: 'Who\'s 30 days late? Who\'s 60? Who owes ACH the most right now? MYOB knows — but you\'re not in MYOB every day. So someone slips.' },
];
function AchLeaks({ onSeen }) {
  return (
    <AchSection id="s-ach-leaks" sectionKey="ach_leaks" index={2} onSeen={onSeen}>
      <AchHeader eyebrow="the leaks" />
      <Reveal><h2 style={{ fontSize: 'clamp(30px, 4vw, 46px)', maxWidth: 720 }}>Six things quietly bleeding ACH every week.</h2></Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginTop: 40 }}>
        {LEAKS.map((l, i) => (
          <Reveal key={i} delay={(i % 3) + 1} className="lift">
            <div className="ach-card" data-testid={`ach-leak-${i}`}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
                <span className="ach-eyebrow" style={{ color: A.brand }}>0{i + 1}</span>
                <h3 style={{ fontSize: 19, color: A.ink }}>{l.t}</h3>
              </div>
              <p style={{ fontFamily: 'Inter', fontSize: 14.5, color: A.ink2, lineHeight: 1.6, margin: 0 }}>{l.b}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </AchSection>
  );
}

// ── 4. THE COST ──────────────────────────────────────────────────────
const COST_STATS = [
  { n: 12, suffix: 'h', label: 'admin hours per week retyping the same data across tabs' },
  { n: 40, suffix: '%', label: 'estimated quotes with no follow-up recorded' },
  { n: 3, suffix: '', label: 'systems your team switches between for one hire' },
  { n: 1, suffix: ' day', label: 'per month someone spends assembling month-end' },
];
function AchCostStat({ n, suffix, label, delay, seen, testid }) {
  const v = useCountUp(n, seen);
  return (
    <Reveal delay={delay}>
      <div className="ach-card" data-testid={testid} style={{ background: A.surface, borderColor: A.border }}>
        <div style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: 56, color: A.brand, lineHeight: 1 }}>{v}{suffix}</div>
        <p style={{ fontFamily: 'Inter', fontSize: 13.5, color: A.ink2, marginTop: 10, lineHeight: 1.55 }}>{label}</p>
      </div>
    </Reveal>
  );
}
function AchCost({ onSeen }) {
  const r = useReveal();
  return (
    <AchSection id="s-ach-cost" sectionKey="ach_cost" index={3} onSeen={onSeen} tone="tint">
      <AchHeader eyebrow="the cost" />
      <Reveal><h2 style={{ fontSize: 'clamp(30px, 4vw, 46px)', maxWidth: 720 }}>The cost of running it the old way.</h2></Reveal>
      <div ref={r.ref} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18, marginTop: 44 }}>
        {COST_STATS.map((s, i) => (
          <AchCostStat key={i} n={s.n} suffix={s.suffix} label={s.label}
            delay={(i % 4) + 1} seen={r.cls === 'is-in'} testid={`ach-cost-stat-${i}`} />
        ))}
      </div>
      <p style={{ fontFamily: 'Inter', fontSize: 13, color: A.ink3, marginTop: 22, fontStyle: 'italic' }}>Rough estimates based on the walkthrough — validated in the shadow month.</p>
    </AchSection>
  );
}

// ── 5. VIBERENT RETIRED ──────────────────────────────────────────────
function AchViberent({ onSeen }) {
  return (
    <AchSection id="s-ach-viberent" sectionKey="ach_viberent" index={4} onSeen={onSeen}>
      <AchHeader eyebrow="viberent, retired" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 40, alignItems: 'start' }}>
        <Reveal>
          <h2 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', maxWidth: 480 }}>Viberent goes away. MYOB stays.</h2>
          <p style={{ fontFamily: 'Inter', fontSize: 15.5, color: A.ink2, marginTop: 18, lineHeight: 1.65 }}>
            Everything Viberent does today — hires, fleet register, agreements — lives natively in the ACH platform.
            MYOB stays as your accounting spine (that connection is proper API, not a copy-paste).
            Viberent runs alongside for a shadow month so nothing skips a beat — then it&apos;s cancelled.
          </p>
        </Reveal>
        <Reveal delay={3} className="lift">
          <div className="ach-card" style={{ background: A.surface }}>
            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
              <div style={{ flex: 1, padding: 14, borderRadius: 10, background: '#f7ecf1', border: `1px solid ${A.brandTint}` }}>
                <p className="ach-eyebrow" style={{ color: A.ink3, fontSize: 10 }}>Today (v1)</p>
                <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: 16, color: A.ink, marginTop: 6 }}>Excel · Viberent · MYOB · email</p>
              </div>
              <div style={{ alignSelf: 'center', color: A.brand, fontSize: 22 }}>→</div>
              <div style={{ flex: 1, padding: 14, borderRadius: 10, background: A.brandTint, border: `1px solid ${A.brand}` }}>
                <p className="ach-eyebrow" style={{ color: A.brand, fontSize: 10 }}>v2 · The ACH Platform</p>
                <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: 16, color: A.ink, marginTop: 6 }}>ACH Hire System + MYOB</p>
              </div>
            </div>
            <p style={{ fontFamily: 'Inter', fontSize: 13.5, color: A.ink2, margin: 0, lineHeight: 1.55 }}>
              Shadow month = both systems run in parallel, ACH picks up every real hire, we reconcile daily.
              Once the numbers agree for a full month, Viberent is cancelled. No cliff, no risk.
            </p>
          </div>
        </Reveal>
      </div>
    </AchSection>
  );
}

// ── 6. AT A GLANCE (mapping table from blueprint §2) ─────────────────
const AT_GLANCE = [
  ['Fleet register', 'Excel + Viberent (double-typed)', 'Live fleet table — status, service, hire, location, WOF'],
  ['Quote build', 'Word/PDF from scratch', 'Rate-card + saved terms → generated PDF, tokenised web version, one-tap accept'],
  ['Hire in progress', 'Viberent + emails + chalk board', 'Hire card — machine, customer, dates, delivery, charge run, service window'],
  ['Charge runs (weekly/monthly)', 'Excel + reminders in head', 'Auto-scheduled charge runs, previewed before push, one-click to MYOB draft'],
  ['Month-end', 'Hunt across tabs, ½–1 day', '"Run month-end" button — anyone can hit it, guided steps, reconciled'],
  ['Invoicing', 'MYOB manual entry', 'Draft invoices auto-created in MYOB via API, girls approve + send'],
  ['Debtors', 'MYOB report, memory', 'Live debtor ladder on the dashboard — colour-coded 30/60/90'],
  ['Service jobs', 'Whiteboard + service book', 'Service jobs tied to machines, scheduled, WOF countdown, one-tap complete'],
  ['Transport jobs', 'Phone + text', 'Transport board — booked jobs, driver, ETA, geo-pin on map'],
  ['Compliance / WOF', 'Diary reminders', 'Compliance panel — every machine, every cert, red before it expires'],
  ['Waitlist / demand', 'Nothing (lost)', 'Waitlist queue — if the crane\'s out, next customer\'s already logged'],
  ['Ownership / entities', 'Split books in head', 'Entity toggle — same platform runs both ACH entities, roll-up view on the dashboard'],
];
function AchAtGlance({ onSeen }) {
  return (
    <AchSection id="s-ach-glance" sectionKey="ach_at_glance" index={5} onSeen={onSeen}>
      <AchHeader eyebrow="at a glance — today vs the platform" />
      <Reveal><h2 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', maxWidth: 720 }}>Everything ACH does today — where it lives after.</h2></Reveal>
      <div style={{ marginTop: 34 }}>
        <p className="ach-mobile-only" style={{ display: 'none', fontFamily: 'Inter', fontSize: 11, color: A.ink3, margin: '0 0 8px', letterSpacing: '.08em' }}>swipe the table →</p>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720, background: A.surface, border: `1px solid ${A.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <thead>
              <tr style={{ background: A.brandTint }}>
                <th style={thS}>What</th>
                <th style={thS}>Today</th>
                <th style={thS}>In the ACH Hire System</th>
              </tr>
            </thead>
            <tbody>
              {AT_GLANCE.map((row, i) => (
                <tr key={i} data-testid={`ach-glance-row-${i}`} style={{ borderTop: `1px solid ${A.border}` }}>
                  <td style={tdS}><b style={{ color: A.ink }}>{row[0]}</b></td>
                  <td style={{ ...tdS, color: A.ink3 }}>{row[1]}</td>
                  <td style={{ ...tdS, color: A.brand, fontWeight: 600 }}>{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AchSection>
  );
}
const thS = { textAlign: 'left', fontFamily: 'Manrope', fontWeight: 700, fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: A.brand, padding: '14px 18px' };
const tdS = { fontFamily: 'Inter', fontSize: 14, color: A.ink2, padding: '14px 18px', verticalAlign: 'top', lineHeight: 1.55 };

// ── Generic mockup showcase section (7-10) ───────────────────────────
function MockSection({ onSeen, sectionKey, index, eyebrow, title, sub, bullets, imgAlt, onEnlarge }) {
  return (
    <AchSection id={`s-${sectionKey}`} sectionKey={sectionKey} index={index} onSeen={onSeen}>
      <AchHeader eyebrow={eyebrow} />
      <Reveal><h2 style={{ fontSize: 'clamp(28px, 3.6vw, 46px)', maxWidth: 720 }}>{title}</h2></Reveal>
      {sub && <Reveal delay={2}><p style={{ fontFamily: 'Inter', fontSize: 16, color: A.ink2, marginTop: 16, maxWidth: 640, lineHeight: 1.65 }}>{sub}</p></Reveal>}
      <Reveal delay={3} className="lift">
        <div className="ach-mock" data-testid={`ach-mock-${sectionKey}`} onClick={() => onEnlarge && onEnlarge({ src: `#${sectionKey}`, alt: imgAlt })} style={{ marginTop: 34 }}>
          <MockPlaceholder title={title} sectionKey={sectionKey} />
        </div>
      </Reveal>
      {bullets && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginTop: 30 }}>
          {bullets.map((b, i) => (
            <Reveal key={i} delay={(i % 4) + 1}>
              <div>
                <span className="ach-eyebrow" style={{ color: A.brand }}>0{i + 1}</span>
                <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: 15, color: A.ink, marginTop: 6 }}>{b.t}</p>
                <p style={{ fontFamily: 'Inter', fontSize: 13.5, color: A.ink2, marginTop: 4, lineHeight: 1.55 }}>{b.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </AchSection>
  );
}

// Lightweight CSS-only mockup placeholder that mirrors the frame's role
// (real screenshots will drop in once the design agent packages them).
function MockPlaceholder({ title, sectionKey }) {
  const isDash = sectionKey === 'ach_dashboard';
  return (
    <div style={{ background: A.surface, minHeight: isDash ? 480 : 360, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 22px', borderBottom: `1px solid ${A.border}`, flexWrap: 'wrap' }}>
        <img src={A_LOGO} alt="All Cranes Hire" style={{ height: 30, width: 'auto', maxWidth: 130, flexShrink: 0 }} />
        <span aria-hidden style={{ display: 'inline-block', width: 1, height: 18, background: A.border }} />
        <span className="ach-eyebrow" style={{ color: A.ink3, fontSize: 10.5 }}>{title}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {['#e3b7cd', '#c5e0d0', '#f0dfb8'].map((c, i) => (
            <span key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
          ))}
        </span>
      </div>
      <div className={`mock-inner${isDash ? ' is-dash' : ''}`} style={{ padding: 22 }}>
        <div>
          <div className="mock-kpi-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            {['on hire · 24', 'quotes · 8', 'overdue · 3', 'this week · $46k'].map((s, i) => (
              <div key={i} style={{ padding: '12px 14px', background: A.brandTint, borderRadius: 10, border: `1px solid ${A.border}` }}>
                <p style={{ fontFamily: 'Inter', fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: A.brand, fontWeight: 700, margin: 0 }}>{s.split('·')[0].trim()}</p>
                <p style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: 22, color: A.ink, margin: '4px 0 0' }}>{s.split('·')[1]?.trim()}</p>
              </div>
            ))}
          </div>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 14px', borderRadius: 10, border: `1px solid ${A.border}`, marginBottom: 8, background: '#fbfaf9' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: i === 0 ? A.brand : i === 1 ? A.ok : A.warn }} />
              <span style={{ flex: 1, height: 8, background: A.border, borderRadius: 999, opacity: 0.6 }} />
              <span style={{ width: 60, height: 8, background: A.border, borderRadius: 999 }} />
            </div>
          ))}
        </div>
        {isDash && (
          <div style={{ background: A.brandTint, borderRadius: 12, padding: 18 }}>
            <p className="ach-eyebrow" style={{ color: A.brand, marginBottom: 10 }}>Needs a decision today</p>
            {['Quote QTE-0182 — Todd', 'Service due · Franna 40T', 'Overdue · JHL Constr.'].map((t, i) => (
              <div key={i} style={{ background: A.surface, padding: '10px 12px', borderRadius: 8, marginBottom: 8, border: `1px solid ${A.border}` }}>
                <p style={{ fontFamily: 'Inter', fontSize: 13, color: A.ink, margin: 0, fontWeight: 600 }}>{t}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 11. Start a hire — 5-step ────────────────────────────────────────
const START_STEPS = [
  ['Customer', 'Pick from the register — or add fresh (name, ABN, PO defaults) once.'],
  ['Machine', 'Pick from live fleet — availability, service window, WOF all visible.'],
  ['Dates + delivery', 'On/off-hire, delivery slot, driver assigned, transport job auto-drafted.'],
  ['Rate + terms', 'Rate card auto-applies. Overrides visible. Terms attached.'],
  ['Sign + go', 'Hire agreement → e-sign. Charge run scheduled. Girls approve first invoice.'],
];
function AchStartHire({ onSeen }) {
  return (
    <AchSection id="s-ach-start" sectionKey="ach_start_hire" index={10} onSeen={onSeen} tone="tint">
      <AchHeader eyebrow="starting a hire · 5 steps" />
      <Reveal><h2 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', maxWidth: 720 }}>From a phone call to a signed hire in under two minutes.</h2></Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 40 }}>
        {START_STEPS.map(([t, b], i) => (
          <Reveal key={i} delay={(i % 5) + 1}>
            <div className="ach-card" data-testid={`ach-hire-step-${i}`} style={{ background: A.surface, position: 'relative' }}>
              <div style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: 40, color: A.brand, opacity: 0.24, lineHeight: 1 }}>0{i + 1}</div>
              <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: 16, color: A.ink, marginTop: 4 }}>{t}</p>
              <p style={{ fontFamily: 'Inter', fontSize: 13.5, color: A.ink2, marginTop: 6, lineHeight: 1.55, margin: '6px 0 0' }}>{b}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </AchSection>
  );
}

// ── 12. Invoices + debtors ladder ────────────────────────────────────
const LADDER = [
  { label: 'Current · 0-30 days', c: A.ok, note: 'Green. Sitting inside terms.' },
  { label: '30-60 days', c: A.warn, note: 'Amber. Auto-nudge from the platform.' },
  { label: '60-90 days', c: '#d4732a', note: 'Deep amber. Girls call. Note on the record.' },
  { label: '90+ days', c: A.err, note: 'Red. Owner-flagged. On the dashboard until resolved.' },
];
function AchInvoices({ onSeen }) {
  return (
    <AchSection id="s-ach-invoices" sectionKey="ach_invoices" index={11} onSeen={onSeen}>
      <AchHeader eyebrow="invoices & debtors" />
      <Reveal><h2 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', maxWidth: 700 }}>You&apos;ll know who&apos;s late before they do.</h2></Reveal>
      <p style={{ fontFamily: 'Inter', fontSize: 15.5, color: A.ink2, marginTop: 16, maxWidth: 640, lineHeight: 1.6 }}>
        Draft invoices land in MYOB automatically after each charge run. Debtors ageing lives on the dashboard — coloured, actionable, honest.
      </p>
      <div style={{ display: 'grid', gap: 10, marginTop: 32 }}>
        {LADDER.map((l, i) => (
          <Reveal key={i} delay={(i % 4) + 1}>
            <div className="ach-card" data-testid={`ach-ladder-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 18, background: A.surface }}>
              <span style={{ width: 10, height: 40, borderRadius: 6, background: l.c, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: 16, color: A.ink, margin: 0 }}>{l.label}</p>
                <p style={{ fontFamily: 'Inter', fontSize: 13.5, color: A.ink3, margin: '4px 0 0' }}>{l.note}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </AchSection>
  );
}

// ── 13. Money flow — 7 steps ─────────────────────────────────────────
const MONEY_STEPS = [
  { t: 'Quote sent', h: false, b: 'PDF + web view + accept button. Tracked.' },
  { t: 'Quote accepted', h: false, b: 'Auto-creates hire, sends confirmation, files signed doc.' },
  { t: 'Hire on', h: false, b: 'Machine flips to "on hire". Charge schedule starts.' },
  { t: 'Charge run', h: true, b: 'Girls preview + approve. One click pushes draft invoices to MYOB.' },
  { t: 'Invoice approved & sent', h: true, b: 'MYOB sends. Debtor ageing starts.' },
  { t: 'Payment lands in MYOB', h: false, b: 'Platform reads MYOB — record marked paid automatically.' },
  { t: 'Hire off + machine back', h: false, b: 'Off-hire, final charge, service window flagged if due.' },
];
function AchMoneyFlow({ onSeen }) {
  return (
    <AchSection id="s-ach-flow" sectionKey="ach_money_flow" index={12} onSeen={onSeen} tone="tint">
      <AchHeader eyebrow="how the money flows" />
      <Reveal><h2 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', maxWidth: 720 }}>Seven steps. Two need a human.</h2></Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginTop: 34 }}>
        {MONEY_STEPS.map((s, i) => (
          <Reveal key={i} delay={(i % 4) + 1}>
            <div className="ach-card" data-testid={`ach-money-step-${i}`} style={{ background: A.surface, borderColor: s.h ? A.brand : A.border, borderWidth: s.h ? 2 : 1 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 6 }}>
                <span className="ach-eyebrow" style={{ color: s.h ? A.brand : A.ink3 }}>0{i + 1}</span>
                {s.h && <span style={{ fontFamily: 'Inter', fontSize: 10, fontWeight: 700, color: A.brand, textTransform: 'uppercase', letterSpacing: '0.14em' }}>· Human</span>}
              </div>
              <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: 15.5, color: A.ink, margin: 0 }}>{s.t}</p>
              <p style={{ fontFamily: 'Inter', fontSize: 13.5, color: A.ink2, margin: '6px 0 0', lineHeight: 1.55 }}>{s.b}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </AchSection>
  );
}

// ── 14. Data model in plain English ──────────────────────────────────
const DATA_MODEL = [
  ['Machine', 'A crane in the fleet. Has a rego, WOF, service schedule, hourly/day/week rate, and a history.'],
  ['Customer', 'A business hiring from ACH. Contact, ABN, PO defaults, payment terms, historic hires.'],
  ['Quote', 'A priced offer. Machine + dates + rate + terms. Web view, PDF, one-click accept.'],
  ['Hire', 'An accepted quote in motion. On-date, off-date, delivery, driver, transport job, charge schedule.'],
  ['Charge run', 'A scheduled invoice event — weekly, monthly, on-hire, off-hire. Previewed, then pushed.'],
  ['Invoice', 'Draft created in MYOB via API. Girls approve, MYOB sends, platform tracks paid/unpaid.'],
  ['Service job', 'A scheduled service against a machine — parts, labour, WOF renewal. Countdown visible.'],
  ['Transport job', 'A delivery or pickup — driver, ETA, geo-pin, ties to a hire.'],
  ['Entity', 'ACH runs two entities. Each hire/quote/invoice belongs to one. Dashboard rolls up both.'],
];
function AchDataModel({ onSeen }) {
  return (
    <AchSection id="s-ach-data" sectionKey="ach_data_model" index={13} onSeen={onSeen}>
      <AchHeader eyebrow="what the system remembers" />
      <Reveal><h2 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', maxWidth: 720 }}>Nine things. Everything else is just a view of them.</h2></Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 30 }}>
        {DATA_MODEL.map((r, i) => (
          <Reveal key={i} delay={(i % 4) + 1}>
            <div className="ach-card" data-testid={`ach-model-${i}`} style={{ background: A.surface }}>
              <p style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: 17, color: A.brand, margin: 0 }}>{r[0]}</p>
              <p style={{ fontFamily: 'Inter', fontSize: 14, color: A.ink2, margin: '8px 0 0', lineHeight: 1.6 }}>{r[1]}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </AchSection>
  );
}

// ── 15. Connectors ───────────────────────────────────────────────────
const CONNECTORS = [
  { t: 'MYOB', b: 'Proper API — customers, draft invoices, payments read-back. Not CSV, not paste.', on: true },
  { t: 'Resend (email)', b: 'Quotes, hire agreements, reminders, statements. Deliverable, trackable.', on: true },
  { t: 'Accessii (e-sign)', b: 'Hire agreements signed on the phone. Signed PDF filed on the hire record.', on: true },
  { t: 'Viberent', b: 'Runs shadow for one month. Then cancelled — no more monthly fee, no more retype.', on: false },
];
function AchConnectors({ onSeen }) {
  return (
    <AchSection id="s-ach-connectors" sectionKey="ach_connectors" index={14} onSeen={onSeen} tone="tint">
      <AchHeader eyebrow="what connects to what" />
      <Reveal><h2 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', maxWidth: 720 }}>Three integrations in. One system out.</h2></Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginTop: 30 }}>
        {CONNECTORS.map((c, i) => (
          <Reveal key={i} delay={(i % 4) + 1}>
            <div className="ach-card" data-testid={`ach-connector-${i}`} style={{ background: A.surface, borderLeft: `4px solid ${c.on ? A.ok : A.ink4}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <p style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: 17, color: A.ink, margin: 0, flex: 1 }}>{c.t}</p>
                <span style={{ fontFamily: 'Inter', fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: c.on ? A.ok : A.ink4 }}>{c.on ? '● Live' : '○ Retired'}</span>
              </div>
              <p style={{ fontFamily: 'Inter', fontSize: 13.5, color: A.ink2, margin: '10px 0 0', lineHeight: 1.6 }}>{c.b}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </AchSection>
  );
}

// ── 16. Migration path ───────────────────────────────────────────────
const MIGRATE = [
  ['Import', 'We pull the fleet, customer register, active hires, and outstanding invoices from Viberent + MYOB. Done in a day.'],
  ['Shadow month', 'ACH platform runs alongside Viberent. Every real hire lands in both. We reconcile daily until the numbers agree.'],
  ['Cut-over', 'Numbers agree. Viberent switches to read-only, ACH is the single source of truth. Old data still queryable.'],
  ['Train', 'One-hour walkthrough for you + the girls + the operators. Written playbook attached. We\'re on call afterwards until it\'s second nature.'],
];
function AchMigration({ onSeen }) {
  return (
    <AchSection id="s-ach-migration" sectionKey="ach_migration" index={15} onSeen={onSeen}>
      <AchHeader eyebrow="getting from here to there" />
      <Reveal><h2 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', maxWidth: 720 }}>Four steps. Zero cliff.</h2></Reveal>
      <div style={{ display: 'grid', gap: 14, marginTop: 32 }}>
        {MIGRATE.map(([t, b], i) => (
          <Reveal key={i} delay={(i % 4) + 1} className="slide">
            <div className="ach-card" data-testid={`ach-migrate-${i}`} style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 20, alignItems: 'start' }}>
              <div style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: 40, color: A.brand, lineHeight: 1 }}>{i + 1}</div>
              <div>
                <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: 20, color: A.ink, margin: 0 }}>{t}</p>
                <p style={{ fontFamily: 'Inter', fontSize: 14.5, color: A.ink2, margin: '8px 0 0', lineHeight: 1.6 }}>{b}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </AchSection>
  );
}

// ── 17. Build roadmap · 4 phases ─────────────────────────────────────
const ROADMAP = [
  { p: 'Phase 1', t: 'Core — fleet, quotes, hires, MYOB', b: 'Fleet register live · quote builder · start-a-hire flow · draft invoices to MYOB · Todd + one girl piloting.' },
  { p: 'Phase 2', t: 'Month-end + charge runs + debtors', b: 'Charge run engine · month-end button · debtors ladder · girls fully independent on invoicing.' },
  { p: 'Phase 3', t: 'Service + transport + compliance', b: 'Service job scheduling · transport board · WOF/compliance panel · waitlist queue.' },
  { p: 'Phase 4', t: 'Polish + cut-over', b: 'Viberent shadow wraps · Viberent cancelled · training complete · playbook handed over.' },
];
function AchRoadmap({ onSeen }) {
  return (
    <AchSection id="s-ach-roadmap" sectionKey="ach_roadmap" index={16} onSeen={onSeen} tone="tint">
      <AchHeader eyebrow="build roadmap · four phases of scope" />
      <Reveal><h2 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', maxWidth: 780 }}>Four phases. All the scope. One promise.</h2></Reveal>

      {/* THE GUARANTEE — the delivery statement MJ leads with. Deliberately
          large + confident, not a badge or an estimate. */}
      <Reveal delay={2}>
        <div data-testid="ach-delivery-guarantee"
          style={{
            marginTop: 34, marginBottom: 34,
            padding: 'clamp(28px, 4vw, 44px) clamp(24px, 4vw, 44px)',
            background: `linear-gradient(120deg, ${A.brand} 0%, ${A.brandLo} 100%)`,
            borderRadius: 18, color: '#fff', position: 'relative', overflow: 'hidden',
            boxShadow: '0 30px 60px -30px rgba(141,27,79,0.5)',
          }}>
          <div style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{
              fontFamily: 'Manrope', fontWeight: 800, fontSize: 'clamp(56px, 8vw, 96px)',
              lineHeight: 1, letterSpacing: '-0.03em', color: '#fff', flexShrink: 0,
            }}>
              4<span style={{ fontSize: '0.5em', fontWeight: 700, marginLeft: 6, letterSpacing: 0 }}>weeks</span>
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <p style={{
                fontFamily: 'Inter', fontSize: 11.5, letterSpacing: '0.22em',
                textTransform: 'uppercase', fontWeight: 700, color: 'rgba(255,255,255,0.72)', margin: 0,
              }}>The delivery promise</p>
              <p style={{
                fontFamily: 'Manrope', fontWeight: 800, fontSize: 'clamp(24px, 3.2vw, 36px)',
                lineHeight: 1.15, color: '#fff', margin: '8px 0 0', letterSpacing: '-0.01em',
              }}>Delivered in 4 weeks — guaranteed.</p>
              <p style={{
                fontFamily: 'Inter', fontSize: 14.5, lineHeight: 1.6, color: 'rgba(255,255,255,0.86)', margin: '10px 0 0', maxWidth: 520,
              }}>Not an estimate. A guarantee. All four phases of scope built and ready for the shadow month inside four weeks of kick-off — signed off, in writing, before we start.</p>
            </div>
          </div>
        </div>
      </Reveal>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
        {ROADMAP.map((r, i) => (
          <Reveal key={i} delay={(i % 4) + 1}>
            <div className="ach-card" data-testid={`ach-phase-${i}`} style={{ background: A.surface, position: 'relative', overflow: 'hidden' }}>
              <p className="ach-eyebrow" style={{ color: A.brand }}>{r.p}</p>
              <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: 17, color: A.ink, margin: '8px 0 0' }}>{r.t}</p>
              <p style={{ fontFamily: 'Inter', fontSize: 13.5, color: A.ink2, margin: '10px 0 0', lineHeight: 1.6 }}>{r.b}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </AchSection>
  );
}

// ── 18. What we need from ACH ────────────────────────────────────────
const ASKS = [
  'MYOB access — read-only API creds to start, write later once we\'re confident.',
  'Viberent export — one CSV dump of fleet + customers + open hires.',
  'One hour with Todd + one girl each week — 30 min feedback, 30 min build direction.',
  'A yes on the shadow month approach (no cliff, both systems run in parallel).',
];
function AchAsk({ onSeen }) {
  return (
    <AchSection id="s-ach-ask" sectionKey="ach_ask" index={17} onSeen={onSeen}>
      <AchHeader eyebrow="what we need from all cranes" />
      <Reveal><h2 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', maxWidth: 720 }}>Small ask. Big return.</h2></Reveal>
      <div style={{ display: 'grid', gap: 12, marginTop: 30 }}>
        {ASKS.map((a, i) => (
          <Reveal key={i} delay={(i % 4) + 1}>
            <div data-testid={`ach-ask-${i}`} style={{ display: 'flex', gap: 16, padding: '16px 20px', background: A.surface, borderRadius: 12, border: `1px solid ${A.border}`, alignItems: 'flex-start' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: A.brandTint, color: A.brand, display: 'grid', placeItems: 'center', fontFamily: 'Manrope', fontWeight: 800, flexShrink: 0 }}>{i + 1}</div>
              <p style={{ fontFamily: 'Inter', fontSize: 15, color: A.ink, margin: 0, lineHeight: 1.55 }}>{a}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </AchSection>
  );
}

// ── 19. FAQ ──────────────────────────────────────────────────────────
const FAQ = [
  { q: 'Are we cutting Viberent too early?', a: 'No — Viberent runs alongside ACH during the shadow run. Every real hire lands in both, and we reconcile daily. Only when the numbers agree does Viberent switch off. It\'s the opposite of a cliff.' },
  { q: 'MYOB API — is it really solid?', a: 'MYOB\'s AccountRight API is proper (not the old ODBC hack). We\'ll set up read-only creds first, then push draft invoices. If the API ever hiccups, drafts queue safely and retry — nothing gets lost.' },
  { q: 'Where does it run and who owns the data?', a: 'It runs on Kindra\'s infrastructure (same stack that runs Kindra AI). Data is 100% ACH\'s — we send a nightly encrypted backup wherever you nominate, and if the arrangement ever ends, you get a full export in an open format. No hostage-taking, ever.' },
  { q: 'Where does Hyjac fit?', a: 'Hyjac is a separate entity in the platform. Its hires, machines, invoices are ring-fenced but roll up to Todd\'s dashboard for the group view. Same platform, two entities, one screen when you want it.' },
  { q: 'Can the girls actually run month-end?', a: 'Yes — that\'s the point. Month-end is a guided button, not a hunt. It walks whoever\'s running it through: charge runs preview → approve → push to MYOB → reconcile → statement send. Anyone who\'s been shown once can do it.' },
];
function AchFAQ({ onSeen }) {
  const [open, setOpen] = useState(0);
  return (
    <AchSection id="s-ach-faq" sectionKey="ach_faq" index={18} onSeen={onSeen} tone="tint">
      <AchHeader eyebrow="straight answers" />
      <Reveal><h2 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', maxWidth: 720 }}>The five questions Todd will ask.</h2></Reveal>
      <div style={{ display: 'grid', gap: 10, marginTop: 30 }}>
        {FAQ.map((f, i) => {
          const isOpen = open === i;
          return (
            <Reveal key={i} delay={(i % 5) + 1}>
              <div data-testid={`ach-faq-${i}`} className="ach-card" style={{ background: A.surface, padding: 0, cursor: 'pointer' }} onClick={() => setOpen(isOpen ? -1 : i)}>
                <div style={{ padding: '18px 22px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <span style={{ color: A.brand, fontFamily: 'Manrope', fontWeight: 800, fontSize: 16, marginTop: 2 }}>{isOpen ? '−' : '+'}</span>
                  <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: 16.5, color: A.ink, margin: 0, flex: 1 }}>{f.q}</p>
                </div>
                {isOpen && (
                  <p style={{ fontFamily: 'Inter', fontSize: 14.5, color: A.ink2, margin: 0, padding: '0 22px 22px 52px', lineHeight: 1.65 }}>{f.a}</p>
                )}
              </div>
            </Reveal>
          );
        })}
      </div>
    </AchSection>
  );
}

// ── 20. KINDRA CLOSE (navy bookend) ──────────────────────────────────
function KindraClose({ onSeen, onCta }) {
  const ref = useSectionTracker('ach_close', 19, onSeen);
  return (
    <section ref={ref} data-section="ach_close" className="k-section" style={{ paddingBottom: 'clamp(70px, 8vw, 110px)' }}>
      <div className="k-spectrum" />
      <div style={{ maxWidth: 1180, margin: '0 auto', position: 'relative' }}>
        <img src={KINDRA_LOGO} alt="Kindra AI" style={{ height: 36, display: 'block', marginBottom: 30 }}
          onError={(e) => { e.target.style.display = 'none'; }} />
        <Reveal>
          <p className="k-eyebrow">the ownership promise</p>
          <h1 style={{ marginTop: 12 }}>Your platform. Your data. Your yard, finally on one screen.</h1>
        </Reveal>
        <Reveal delay={2}>
          <p className="k-lede" style={{ marginTop: 22 }}>
            The system and every record inside it belong to All Cranes Hire. Nightly encrypted backups
            in a format you can open elsewhere. If our arrangement ever ends, you get a full export —
            no held-hostage data, no lock-in. That&apos;s the promise.
          </p>
        </Reveal>
        <Reveal delay={3}>
          <div data-testid="ach-close-guarantee" style={{
            marginTop: 30, display: 'inline-flex', alignItems: 'center', gap: 14,
            padding: '14px 22px', borderRadius: 999,
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(220,240,245,0.24)',
            backdropFilter: 'blur(6px)',
          }}>
            <span style={{
              fontFamily: 'Manrope', fontWeight: 800, fontSize: 30, lineHeight: 1,
              color: '#fff', letterSpacing: '-0.02em',
            }}>4 weeks</span>
            <span aria-hidden style={{ display: 'inline-block', width: 1, height: 22, background: 'rgba(220,240,245,0.28)' }} />
            <span style={{
              fontFamily: 'Inter', fontSize: 13, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: '#fff', fontWeight: 700,
            }}>Delivered · guaranteed</span>
          </div>
        </Reveal>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 44 }}>
          <button data-testid="ach-cta-lets-build" className="ach-btn primary" onClick={() => onCta('lets_build')} style={{ background: '#fff', color: K.navy, fontWeight: 700 }}>Let&apos;s build it →</button>
          <button data-testid="ach-cta-questions" className="ach-btn ghost" onClick={() => onCta('questions_first')} style={{ background: 'transparent', color: K.icy, borderColor: 'rgba(220,240,245,0.4)' }}>Questions first</button>
        </div>
        <div style={{ marginTop: 60, paddingTop: 30, borderTop: '1px solid rgba(220,240,245,0.14)', fontFamily: 'Inter', fontSize: 12, color: K.icyDim, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
          KINDRA AI &nbsp;·&nbsp; BRISBANE &nbsp;·&nbsp; michael@kindraai.dev
        </div>
      </div>
    </section>
  );
}

// ── CTA modal + Lightbox ─────────────────────────────────────────────
function CtaModal({ open, cta, onClose, onSubmit }) {
  const [name, setName] = useState(''); const [email, setEmail] = useState(''); const [note, setNote] = useState(''); const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setName(''); setEmail(''); setNote(''); setBusy(false); } }, [open]);
  if (!open) return null;
  const title = cta === 'lets_build' ? "Let's build it" : 'Questions first';
  const sub = cta === 'lets_build' ? 'Great. Drop a note and Michael will be in touch inside the day.' : 'Anything on your mind — fire it through.';
  return (
    <div className="ach" style={{ position: 'fixed', inset: 0, background: 'rgba(15,10,25,0.7)', backdropFilter: 'blur(6px)', zIndex: 90, display: 'grid', placeItems: 'center', padding: 20 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, maxWidth: 480, width: '100%', padding: 30, border: `1px solid ${A.border}` }}>
        <p className="ach-eyebrow">{cta === 'lets_build' ? "LET'S BUILD" : 'QUESTIONS FIRST'}</p>
        <h2 style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: 26, color: A.ink, margin: '8px 0 8px' }}>{title}</h2>
        <p style={{ fontFamily: 'Inter', fontSize: 14, color: A.ink2, margin: 0, lineHeight: 1.5 }}>{sub}</p>
        <div style={{ marginTop: 18, display: 'grid', gap: 10 }}>
          <input data-testid="ach-modal-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" style={achInp} />
          <input data-testid="ach-modal-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (so we can reply)" style={achInp} />
          <textarea data-testid="ach-modal-note" value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Anything you'd like Michael to know…" style={{ ...achInp, resize: 'vertical' }} />
        </div>
        <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="ach-btn ghost">Cancel</button>
          <button data-testid="ach-modal-send" disabled={busy} className="ach-btn primary"
            onClick={async () => { setBusy(true); const ok = await onSubmit({ name, email, note }); setBusy(false); if (ok) onClose(); }}>
            {busy ? 'Sending…' : 'Send to Michael'}
          </button>
        </div>
      </div>
    </div>
  );
}
const achInp = { background: '#fff', border: `1px solid ${A.border}`, color: A.ink, padding: '12px 14px', fontFamily: 'Inter', fontWeight: 400, fontSize: 14.5, borderRadius: 10, outline: 'none' };

function Lightbox({ item, onClose }) {
  useEffect(() => { if (!item) return; const onKey = (e) => e.key === 'Escape' && onClose(); document.addEventListener('keydown', onKey); const prev = document.body.style.overflow; document.body.style.overflow = 'hidden'; return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; }; }, [item, onClose]);
  if (!item) return null;
  return (
    <div className="ach ach-lb" data-testid="ach-lightbox" onClick={onClose}>
      <div className="ach-lb-bar" onClick={(e) => e.stopPropagation()}>
        <span className="ach-eyebrow" style={{ color: '#fff' }}>ACH HIRE SYSTEM · {item.alt}</span>
        <button className="ach-lb-close" data-testid="ach-lightbox-close" onClick={onClose}>×</button>
      </div>
      <div className="ach-lb-scroll" onClick={(e) => e.stopPropagation()}>
        <div style={{ maxWidth: 1200, width: '100%', background: A.surface, borderRadius: 12, padding: 30 }}>
          <MockPlaceholder title={item.alt} sectionKey="ach_dashboard" />
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────
export default function V2AchPitchPage({ pitch, onEvent, onMessage }) {
  const [modal, setModal] = useState(null);
  const [ctaState, setCtaState] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [progress, setProgress] = useState(0);
  const seenSections = useRef(new Set());

  useEffect(() => {
    document.title = `All Cranes Hire — the ACH Hire System · Prepared for ${pitch?.prepared_for || 'Todd'}`;
    const m = document.createElement('meta'); m.name = 'robots'; m.content = 'noindex,nofollow,noarchive'; document.head.appendChild(m);
    return () => { try { document.head.removeChild(m); } catch { /* noop */ } };
  }, [pitch?.prepared_for]);

  // Scroll progress
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setProgress(max > 0 ? Math.min(1, h.scrollTop / max) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Section-seen tracker + heartbeat
  const reportSection = useCallback((section, index) => {
    if (seenSections.current.has(section)) return;
    seenSections.current.add(section);
    onEvent && onEvent({ kind: 'section', section, index });
  }, [onEvent]);
  useEffect(() => {
    if (!onEvent) return;
    const t = setInterval(() => { if (!document.hidden) onEvent({ kind: 'heartbeat', seconds: 15 }); }, 15000);
    return () => clearInterval(t);
  }, [onEvent]);

  const openCta = (cta) => { setCtaState(cta); setModal(cta); onEvent && onEvent({ kind: 'cta', cta }); };
  const submitCta = async ({ name, email, note }) => {
    if (!onMessage) return true;
    return await onMessage({ cta: ctaState, name, email, note });
  };

  return (
    <div className="ach" style={{ background: A.bg, minHeight: '100vh' }}>
      <AchStyles />
      <div className="ach-progress" style={{ width: `${progress * 100}%` }} />

      <KindraIntro onSeen={reportSection} prepared_for={pitch?.prepared_for} />
      <AchHero onSeen={reportSection} />
      <AchLeaks onSeen={reportSection} />
      <AchCost onSeen={reportSection} />
      <AchViberent onSeen={reportSection} />
      <AchAtGlance onSeen={reportSection} />
      <MockSection onSeen={reportSection} sectionKey="ach_dashboard" index={6}
        eyebrow="the dashboard · morning screen" title="Everything ACH cares about, in one glance."
        sub="Needs a decision today · on-hire-now · month-end status · compliance countdown · waitlist queue. The screen Todd opens with his coffee."
        onEnlarge={setLightbox} imgAlt="The ACH morning dashboard"
        bullets={[
          { t: 'Needs a decision today', b: 'Quotes to price, hires to sign off, invoices to approve — surfaced, not buried.' },
          { t: 'On-hire, right now', b: 'Every crane currently on hire — customer, dates, delivery, days left.' },
          { t: 'Month-end status', b: 'How close to reconciled. What\'s outstanding. Coloured, honest.' },
          { t: 'Compliance + waitlist', b: 'WOFs expiring in the next 30 days · customers waiting on cranes.' },
        ]} />
      <MockSection onSeen={reportSection} sectionKey="ach_fleet" index={7}
        eyebrow="the fleet register" title="Every crane. Every status. One table."
        sub="No more Excel guessing. Availability, service window, WOF expiry, location, hire history — one row per machine, always current."
        onEnlarge={setLightbox} imgAlt="Fleet register"
        bullets={[
          { t: 'Live availability', b: 'Green / amber / red — bookable today, held, on hire.' },
          { t: 'Service countdown', b: 'Hours until next service. Amber at 90%. Red at overdue.' },
          { t: 'WOF countdown', b: 'Days until certification expiry. Auto-books renewal 30 days out.' },
          { t: 'Full hire history', b: 'Every hire this machine\'s been on — customer, dates, revenue, service after.' },
        ]} />
      <MockSection onSeen={reportSection} sectionKey="ach_quotes" index={8}
        eyebrow="quotes + waitlist" title="Every quote. Every stage. Every open."
        sub="Draft → sent → viewed → accepted / declined / silent. Waitlist queue for when the crane\'s already out."
        onEnlarge={setLightbox} imgAlt="Quote pipeline + waitlist"
        bullets={[
          { t: 'One rate card, always current', b: 'Update it once. Every future quote reads from it.' },
          { t: 'Web + PDF quote', b: 'Client gets a tokenised link they can accept in one tap — and a PDF for the paperwork.' },
          { t: 'Open + section tracking', b: 'You see who read what, and when. Follow-ups get drafted automatically after 3 days.' },
          { t: 'Waitlist rescue', b: 'Crane out? Customer joins the waitlist. Auto-nudged when it\'s back.' },
        ]} />
      <MockSection onSeen={reportSection} sectionKey="ach_month_end" index={9}
        eyebrow="month-end · the button" title="Any of the girls can run it."
        sub="Guided steps. Preview. Approve. Push to MYOB. Reconciled. What used to take a day now takes an hour, and it\'s the same procedure every time."
        onEnlarge={setLightbox} imgAlt="Month-end engine"
        bullets={[
          { t: 'One button, five steps', b: 'Charge runs · approvals · MYOB push · statements · reconcile.' },
          { t: 'Preview before push', b: 'Every invoice previewed. Girls can adjust before anything hits MYOB.' },
          { t: 'Reconciliation view', b: 'Live tally of what agrees with MYOB and what doesn\'t. Fix the mismatches in one screen.' },
          { t: 'Anyone can run it', b: 'Same procedure every time. Two girls trained means never a bottleneck.' },
        ]} />
      <AchStartHire onSeen={reportSection} />
      <AchInvoices onSeen={reportSection} />
      <AchMoneyFlow onSeen={reportSection} />
      <AchDataModel onSeen={reportSection} />
      <AchConnectors onSeen={reportSection} />
      <AchMigration onSeen={reportSection} />
      <AchRoadmap onSeen={reportSection} />
      <AchAsk onSeen={reportSection} />
      <AchFAQ onSeen={reportSection} />
      <KindraClose onSeen={reportSection} onCta={openCta} />

      <CtaModal open={!!modal} cta={modal} onClose={() => setModal(null)} onSubmit={submitCta} />
      <Lightbox item={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
